import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getConnections } from "@/helpers/apis";
import { ConnectionsQueryKey } from "@/pages/mcp/constants";
import { useAppState } from "@/modules/app/useAppContext";

interface ConnectionsHookOptions {
  /**
   * Filter connections for specific integration IDs only
   */
  integrations?: string[];
}

/**
 * Hook to consolidate connections fetching and selection logic
 * - Fetches connections and selects first connection per integration
 * - allows selecting different connections per integration
 * @param options
 * @returns
 */
export const useConnections = (options: ConnectionsHookOptions = {}) => {
  const { loggedinUser } = useAppState();
  const [selectedConnections, setSelectedConnections] = useState<Record<string, string>>({});

  // Fetch connections data
  const {
    data: connectionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [ConnectionsQueryKey],
    queryFn: () => getConnections(),
    enabled: !!loggedinUser,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes (no refetch on remount)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const connections = useMemo(() => {
    const allConnections = connectionsData?.connections?.filter(conn => conn.id) || [];

    // Filter for specific integrations if provided
    if (options.integrations) {
      return allConnections.filter(conn => options.integrations?.includes(conn.integration_id));
    }

    return allConnections;
  }, [connectionsData, options.integrations]);

  // Create map of first connection per integration (this is the default selection)
  const firstConnectionByIntegrationId = useMemo(() => {
    return connections.reduce(
      (acc, connection) => {
        // Set the first connection id for each integration id
        if (!acc[connection.integration_id] && connection.integration_id && connection.id) {
          acc[connection.integration_id] = connection.id!;
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [connections]);

  // Handle connection selection
  const handleConnectionSelect = (connectionId: string, integrationId: string) => {
    setSelectedConnections(prev => ({
      ...prev,
      [integrationId]: connectionId,
    }));
  };

  // Merge defaults with user selections (user selections override defaults)
  const finalSelectedConnections = useMemo(() => {
    return { ...firstConnectionByIntegrationId, ...selectedConnections };
  }, [firstConnectionByIntegrationId, selectedConnections]);

  return {
    connections,
    isLoading,
    error,
    selectedConnections: finalSelectedConnections,
    handleConnectionSelect,
  };
};
